import React from "react";
import { Button, Input } from "@/components/ui";
import { Plus, Trash2, GripVertical } from "lucide-react";
import { cn } from "@/lib/utils";

interface ChoicesManagerProps {
  value: string[];
  onChange: (value: string[]) => void;
}

export function ChoicesManager({ value, onChange }: ChoicesManagerProps) {
  const choices = value && Array.isArray(value) ? value : [];
  const [draggedChoiceIndex, setDraggedChoiceIndex] = React.useState<number | null>(null);

  const handleAddChoice = () => {
    const nextNum = choices.length + 1;
    onChange([...choices, `Option ${nextNum}`]);
  };

  const handleRemoveChoice = (index: number) => {
    const updated = choices.filter((_, i) => i !== index);
    onChange(updated);
  };

  const handleEditChoice = (index: number, text: string) => {
    const updated = [...choices];
    updated[index] = text;
    onChange(updated);
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedChoiceIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedChoiceIndex === null || draggedChoiceIndex === index) return;

    const updated = [...choices];
    const draggedItem = updated[draggedChoiceIndex];
    updated.splice(draggedChoiceIndex, 1);
    updated.splice(index, 0, draggedItem);
    setDraggedChoiceIndex(index);
    onChange(updated);
  };

  const handleDragEnd = () => {
    setDraggedChoiceIndex(null);
  };

  return (
    <div className="mt-4 border-t border-slate-200/60 pt-4">
      <div className="flex items-center justify-between mb-3 select-none">
        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Answer Choices</h4>
        <Button variant="outline" size="xs" className="h-7 text-xs" type="button" onClick={handleAddChoice}>
          <Plus className="mr-1 size-3" />
          Add Choice
        </Button>
      </div>

      <div className="space-y-2">
        {choices.map((choice, cIdx) => {
          const isDragging = draggedChoiceIndex === cIdx;
          return (
            <div
              key={cIdx}
              draggable
              onDragStart={e => handleDragStart(e, cIdx)}
              onDragOver={e => handleDragOver(e, cIdx)}
              onDragEnd={handleDragEnd}
              className={cn(
                "flex items-center gap-2 bg-white rounded-lg border border-slate-200 p-2 transition-all duration-150 shadow-sm",
                isDragging
                  ? "opacity-55 scale-[0.98] border-primary/40 bg-primary/5 ring-2 ring-primary/10"
                  : "hover:border-slate-300"
              )}
            >
              <div className="text-slate-400 cursor-grab active:cursor-grabbing hover:text-slate-600 p-1">
                <GripVertical className="size-3.5" />
              </div>

              <span className="text-xs font-semibold text-slate-400 bg-slate-50 border border-slate-100 px-1.5 py-0.5 rounded select-none">
                {cIdx + 1}
              </span>

              <Input
                value={choice}
                onChange={e => handleEditChoice(cIdx, e.target.value)}
                placeholder={`Choice ${cIdx + 1}`}
                className="flex-1 bg-white h-8 text-xs font-sans py-1"
              />

              {choices.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveChoice(cIdx)}
                  className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 p-1.5 rounded transition-all duration-200"
                  title="Delete Choice"
                >
                  <Trash2 className="size-3.5" />
                </button>
              )}
            </div>
          );
        })}

        {choices.length === 0 && (
          <div className="text-center py-4 bg-muted/25 rounded-lg border border-dashed border-slate-200">
            <p className="text-xs text-slate-400 font-sans">No choices defined yet. Click "Add Choice" to begin.</p>
          </div>
        )}
      </div>
    </div>
  );
}
